import { applyStairTravel, stairTravelForRendering } from "./effects/stair_travel"
import { applyFloorFall, floorFallZIndex, floorFallForRendering } from "./effects/floor_fall"
import { GroundRupture } from "./effects/pre_spawn_effect"
import { AreaProjectile } from "./sprites/area_projectile"
import { ItemTypeMeta } from "game-common/item/item"
import { InteractionDescriptor, InteractionType, interactionLabel, interactionIsHeld, rankInteractions, normalizeInteractions } from "game-common/mechanics/entity_mechanics"
import {
    AnimatedFeedbackParams,
    Coordinate,
    EmberRosterRole,
    Entity,
    EntityId,
    LightsContainer,
    Location,
    LocationIcon,
    SIGIL_FLAG,
    TileSize,
    WeaponType,
} from "game-common/models"
import { Application, Container, Graphics } from "pixi.js"

import { ClientGameLogic } from "../../../client_game_logic"
import { IEntityRenderingManager, SpokenLineOptions } from "../../../client_models"
import { MinimapSurface } from "./minimap_surface"
import { PixiJsClientRenderer } from "./pixijs_client_renderer"
import { BaseRenderable } from "./sprites/base_renderable"
import { EntityHighlight, HighlightFrame } from "./sprites/entity_highlight"
import { StatusHaloTier, statusHaloTier } from "./sprites/member_status_halo"
import { BasicSprite } from "./sprites/basic_sprite"
import { Blank } from "./sprites/blank"
import { Bomb } from "./sprites/bomb"
import { Bullet } from "./sprites/bullet"
import { FireSprite } from "./sprites/fire_sprite"
import { entityAppearanceKey, updateEntityArtwork } from "./entity_artwork"
import { Goal } from "./sprites/goal"
import { LpcComposite } from "./sprites/lpc_composite"
import { Other } from "./sprites/other"
import { Plus } from "./sprites/plus"
import { SignSprite } from "./sprites/sign_sprite"
import { WorldOverlaySurface } from "./world_overlay_surface"
import { Callback } from "game-common/util"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { Mechanics } from "game-common/mechanics/mechanics"
import { isMobile } from "../../../client_util"
import { FootDust } from "./effects/foot_dust"
import { PlayerOcclusionOutline } from "./sprites/player_occlusion_outline"
import { CommandWheel } from "./gui/command_wheel"
import { GrassFeet, grassAt } from "./effects/grass_feet"

export type ClientRenderable = Partial<BaseRenderable> & Graphics

// How long an entity stays "pointed at" after the pointer leaves it. There are no interaction icons
// to travel onto any more, but the grace period still absorbs the pointer flicking across a gap in
// an entity's hit area, and it keeps the hide self-healing if a pointer-out event is ever dropped.
const INTERACTION_HOVER_GRACE_MS = 150

// Touch has no hover, so the interactable nearest the player is focused instead: it takes the same
// outline the pointer would give it, which is the only cue a tap has anything to activate. Recomputed
// on this interval rather than per frame -- it walks the room's interactable entities.
const FOCUS_SCAN_INTERVAL_MS = 200
// How close the player has to be for an entity to become the focused one. Generous compared with the
// server's own reach checks (1.5-2 tiles), so the outline appears slightly before the interaction
// would actually succeed rather than after.
const FOCUS_RANGE = TileSize * 2.5

export const EntityRenderActionTypes = ["move", "idle", "attack"] as const
export type EntityRenderActions = (typeof EntityRenderActionTypes)[number]

export interface SpriteMeta {
    name?: string
    scale: number
    speed?: number
    animated?: boolean
    color?: number
    alpha?: number
    supportLabel?: boolean
    rotating?: boolean
    looping?: boolean
    onLoopDone?: () => void
    animationSpeed?: number
    rotation?: number
    spriteScale?: number
    tileMapMetaProvider?: TileMapMetaProvider
    isSubmerged?: boolean
}

export interface EntityRenderMeta {
    renderActions: Partial<Record<EntityRenderActions, SpriteMeta>>
    alwaysShowLabel?: boolean
    disallowMoveAttackAnimation?: boolean
    spriteRootName?: string
}

interface EntityMeta {
    name: string
    color: number
    hp: number
    hunger: number
    maxHunger: number
    maxHp: number
    mana: number
    visible: boolean
    // Watched because the HUD draws a FALLEN player's health as empty whatever their hp says.
    downed?: boolean
    invading?: boolean
    level: number
    speed: number
    joinedTeam: boolean
    paralyzeUntilTs: number
    upgradePoints: number
    weaponType?: WeaponType
    apperanceKey?: string
    x: number
    y: number
    expirationTs?: number
    clickable: boolean
    quantity?: number
    spriteScale?: number
    spriteId?: string
    leaderEntityId?: EntityId
    sigil?: string
    submerged?: boolean
    afk?: boolean
    role?: EmberRosterRole | null
    statusHaloTier?: StatusHaloTier
}

// An entity with no colour is drawn untinted. Handing a renderable `undefined` becomes a PIXI tint
// of 0, which keeps the art's alpha and blacks out its colour: the campfire logs turned into a
// black silhouette after a live publication rebuilt them (refreshContent replays `visible`).
const tintOf = (color?: number): number => color ?? 0xffffff

export class EntityRenderingManager implements IEntityRenderingManager {
    private surface: Container
    private entityIdToRenderable: Record<EntityId, ClientRenderable> = {}
    private logic: ClientGameLogic
    private localEntityMetaCache: Record<EntityId, EntityMeta> = {}
    // Last interaction list the server told us about, keyed by entityId and cached
    // independently of the renderable. The list can arrive (proactive broadcast or the
    // response to a hover re-request) before the client has built the entity's renderable,
    // so it is stashed here and applied whenever the renderable turns up. This cache is also
    // what decides, locally and with no round trip, what a tap on the entity does: see
    // resolveInteractions in game-common and PlayerController's press arbitration.
    private collidingInteractionEntities = new Set<EntityId>()
    private defaultInteractionWheels = new Map<EntityId, CommandWheel>()
    private defaultInteractionLabels = new Map<EntityId, string>()
    private entityIdToInteractions: Record<EntityId, InteractionDescriptor[]> = {}
    // The entity currently wearing the hover/focus outline, so a new one can clear the old.
    private highlightedEntityId?: EntityId
    // A SECOND, independent reason to wear the outline: this entity is the ember roster card the
    // player has selected. Tracked apart from the hover channel above because either can come and go
    // while the other holds -- pointing at something else and away again must not strip the outline
    // off the member whose command wheel is still up, and vice versa.
    private rosterHighlightId?: EntityId
    // Outline for a highlighted entity whose art is map tiles rather than a sprite (see tileHighlight).
    private entityTileHighlight?: EntityHighlight
    private lastFocusScanTs: number = 0
    private clientRenderer: PixiJsClientRenderer
    private minimapSurface: MinimapSurface
    private worldOverlaySurface: WorldOverlaySurface
    private lights: LightsContainer = new LightsContainer()
    private sceneLights = new LightsContainer()
    // A renderable is only ever hidden by render(), and the render loop only calls render() for
    // the entities in that frame's culled set. An entity that leaves the set between two frames --
    // it wandered out of the viewport, or the follow-cam PiP stopped feeding its neighborhood --
    // was therefore never told to hide, and its sprite stayed drawn at the position it was last
    // rendered at while the real entity moved on: the "ghost" NPC. These two sets let
    // beginFrame/endFrame hide anything that was on screen last frame and went unrendered this one.
    private shownEntityIds: Set<EntityId> = new Set()
    private renderedThisFrame: Set<EntityId> = new Set()
    // Keep completion until entity removal: stale snapshots and art rebuilds must not replay an arrival.
    private completedPreSpawnEffects = new Set<EntityId>()
    private preSpawnEffects = new Map<EntityId, GroundRupture>()
    private levelTransferArt = new Map<EntityId, LpcComposite>()
    private reusedLevelArt = new WeakSet<ClientRenderable>()
    private transferringLevel = false
    private levelTransferExpiry?: ReturnType<typeof setTimeout>
    private playerLightHandoff?: { offset?: { x: number; y: number }; startTs?: number }

    beginLevelTransfer = (enabled: boolean, elevationChange = 0) => {
        this.clearLevelTransferArt()
        this.transferringLevel = enabled
        this.playerLightHandoff = undefined
        const id = this.logic.playerEntity?.entityId
        const light = id && this.lights.get(id)
        if (enabled && light) {
            // The destination's local coordinates shift along with the camera.
            // Preserve the carried light's screen position while arrival data is in flight.
            this.lights.set(id, { ...light, location: { x: light.location.x, y: light.location.y + elevationChange } })
            this.playerLightHandoff = {}
        }
    }

    endLevelTransfer = () => {
        this.transferringLevel = false
        // Active-room entities follow the map packet. Give those messages time to adopt
        // the already drawn bodies; discard anything no longer in either snapshot.
        this.levelTransferExpiry = setTimeout(() => this.clearLevelTransferArt(), 1000)
    }

    private clearLevelTransferArt = () => {
        if (this.levelTransferExpiry !== undefined) clearTimeout(this.levelTransferExpiry)
        this.levelTransferExpiry = undefined
        this.levelTransferArt.forEach(sprite => { sprite.cleanup(); sprite.destroy({ children: true }) })
        this.levelTransferArt.clear()
    }

    /** Filters whose shaders are worth compiling at startup (see warmFilterShaders). */
    warmableFilters = () => [this.playerOcclusionOutline.filter]

    retainLevelEntity = (entity: Entity, sprite: ClientRenderable): boolean => {
        if (!this.transferringLevel || !(sprite instanceof LpcComposite)) return false
        sprite.detachWorldOverlayItem()
        sprite.parent?.removeChild(sprite)
        this.levelTransferArt.set(entity.entityId, sprite)
        return true
    }

    private takeLevelEntity = (entity: Entity): LpcComposite | undefined => {
        const sprite = this.levelTransferArt.get(entity.entityId)
        if (!sprite) return undefined
        this.levelTransferArt.delete(entity.entityId)
        this.reusedLevelArt.add(sprite)
        sprite.alpha = 1
        sprite.interactiveChildren = true
        return sprite
    }
    // The dust a running body kicks up. It lives in the world surface rather than in the entity's
    // own feedback channel so the puffs z-sort against the bodies they are dropped between -- see
    // FootDust, and `feedback` below for how the server's trail events get here.
    private footDust: FootDust
    private grassFeet: GrassFeet
    private playerOcclusionOutline: PlayerOcclusionOutline

    constructor(
        surface: Container,
        minimapSurface: MinimapSurface,
        worldOverlaySurface: WorldOverlaySurface,
        logic: ClientGameLogic,
        clientRenderer: PixiJsClientRenderer,
        app: Application,
    ) {
        this.surface = surface
        this.playerOcclusionOutline = new PlayerOcclusionOutline(surface)
        this.minimapSurface = minimapSurface
        this.worldOverlaySurface = worldOverlaySurface
        this.logic = logic
        this.clientRenderer = clientRenderer
        this.footDust = new FootDust(surface, FootDust.textureFactory(app.renderer as any))
        this.grassFeet = new GrassFeet(surface, (x, y) => grassAt(this.logic.tilemapMetaProvider, x, y))
    }

    init = () => {
        // noop, todo?
    }

    cacheEntity = (entity: Entity) => {
        const item: EntityMeta = {
            name: entity.name,
            color: entity.color,
            hp: entity.hp,
            hunger: entity.hunger,
            maxHunger: entity.maxHunger,
            maxHp: entity.maxHp,
            mana: entity.mana,
            visible: entity.visible,
            level: 0,
            speed: entity.speed,
            joinedTeam: undefined,
            paralyzeUntilTs: entity.paralyzeUntilTs,
            upgradePoints: entity.upgradePoints,
            apperanceKey: this.computeAppearanceKey(entity),
            weaponType: entity.weapon?.weaponType,
            x: entity.location.x,
            y: entity.location.y,
            expirationTs: entity.expirationTs,
            clickable: entity.clickable,
            quantity: entity.quantity,
            leaderEntityId: entity.leaderEntityId,
        }
        this.localEntityMetaCache[entity.entityId] = item
        return item
    }

    computeAppearanceKey = entityAppearanceKey

    isPaperdollDifferent = (cachedEntity: EntityMeta, entity: Entity): boolean => {
        return cachedEntity.apperanceKey !== this.computeAppearanceKey(entity)
    }

    renderEntity = (entity: Entity, meta?: SpriteMeta): ClientRenderable => {
        const retained = this.takeLevelEntity(entity)
        if (retained) return retained
        let renderable: ClientRenderable
        switch (entity.npcType) {
            case "flag": {
                renderable = new BasicSprite({ color: entity.color, name: "flag", scale: 0.6, animated: true })
                break
            }
            case "spawner": {
                renderable = new BasicSprite({ color: 0xffffff, name: "spawner", scale: 2.0, animated: true })
                break
            }
            case "bomb": {
                renderable = new BasicSprite({
                    color: 0xffffff,
                    name: "bomb.png",
                    scale: 1.0,
                    animated: false,
                    rotating: true,
                    ...(meta || {}),
                })
                break
            }
            case "emp": {
                renderable = new BasicSprite({
                    color: 0xffffff,
                    name: "emp.png",
                    scale: 1.0,
                    animated: false,
                    rotating: true,
                })
                break
            }
            case "sign": {
                renderable = new SignSprite({
                    color: entity.color,
                    name: "sign.png",
                    scale: 0.55,
                    animated: false,
                })
                break
            }
            case "goal": {
                renderable = new Goal(entity.color)
                break
            }
            case "light": {
                renderable = new Blank()
                break
            }
            case "door": {
                renderable = new BasicSprite({ name: "arch.png", scale: 2.0, animated: false })
                // renderable = new BasicSprite(entity.color, "arch", 2)
                renderable.zIndex = 1000000
                break
            }
            case "lock": {
                renderable = new BasicSprite({ name: "lock.png", scale: 0.5, animated: false })
                break
            }
            case "fire": {
                renderable = new BasicSprite({ color: 0xff0000, name: "fire", scale: 1.0, animated: true })
                break
            }
            case "health":
                renderable = new Plus(0x00ff00, true)
                break
            case "mana": {
                renderable = new Plus(0x399cbd, true)
                break
            }
            case "outpost": {
                renderable = new BasicSprite({
                    name: entity.spriteId,
                    scale: 1.0,
                    color: 0xffffff,
                    rotating: entity.expirationTs !== undefined,
                })
                break
            }
            default: {
                renderable = new LpcComposite(entity.entityId)
            }
        }

        return renderable
    }

    private createCollectableArtwork = (entity: Entity): BasicSprite => {
        const sprite = new BasicSprite({ name: entity.spriteId, scale: 1, color: 0xffffff,
            animated: false, supportLabel: true, rotating: entity.expirationTs !== undefined })
        sprite.container.rotation = entity.movement.angle
        if (entity.quantity) sprite.updateName(`${entity.quantity}`)
        return sprite
    }

    private createMarkerArtwork = (entity: Entity, withClickArea = false): BaseRenderable => {
        const sprite = entity.spriteId === "fire"
            ? new FireSprite()
            : new BasicSprite({ name: entity.spriteId, scale: 1, color: 0xffffff,
                tileMapMetaProvider: this.logic.tilemapMetaProvider }, withClickArea)
        if (sprite instanceof FireSprite) sprite.setSputtering(!!entity.rainDamaged)
        sprite.rotation = entity.movement.angle
        return sprite
    }

    /** Artwork only: companion-room bodies have no active-room collision, hover, HUD or input. */
    renderSceneEntity = (entity: Entity): ClientRenderable => {
        const retained = this.takeLevelEntity(entity)
        if (retained) return retained
        if (entity.npcType === "marker" && entity.spriteId) {
            return this.createMarkerArtwork(entity)
        }
        if (entity.npcType === "collectable") return this.createCollectableArtwork(entity)
        if (entity.entityType === "player") return new LpcComposite(entity.entityId, undefined, false, true)
        // Melee attacks use bullet hitboxes with no artwork, including on passive floors.
        if (entity.entityType === "bullet") return entity.spriteId === "none" ? new Blank() : this.createBulletArtwork()
        if (entity.entityType === "bomb") return new Bomb(entity)
        if (entity.areaProjectile) return new AreaProjectile(entity)
        return this.renderEntity(entity)
    }

    attachClickHandler = (entity: Entity, entityRenderable: ClientRenderable) => {
        const renderable = entityRenderable.getClickSurface()

        renderable.interactive = this.shouldTakePointer(entity)

        // Re-apply whatever the server has already told us about this entity. The list can arrive
        // before the renderable exists (a proactive broadcast, or the reply to a hover request that
        // won the race), so applying it here -- at create and on every clickable change -- arms the
        // entity regardless of message ordering. Without this the entity silently stays inert until
        // some later hover happens to win the round trip, which was the intermittent "interactions
        // don't work on that one NPC".
        this.applyInteractionAffordance(entity.entityId)

        // update() re-invokes this whenever entity.clickable changes. Bind the pointer listeners
        // (and the hover-state closure below) only once per renderable -- otherwise stacked
        // listeners fight each other over the same hover state.
        if (renderable.interactionsBound) {
            return
        }
        renderable.interactionsBound = true

        // Hover state for one entity. The icons this used to arm are gone: hovering now shows the
        // entity's own name/HP and, when it has something to activate, the warm outline. The grace
        // timer on the way out is kept because it makes the hide self-healing -- a dropped
        // pointer-out still resolves to "not hovered" instead of latching the outline on forever.
        let overSelf = false
        let hideTimer: ReturnType<typeof setTimeout> | undefined

        const clearHideTimer = () => {
            if (hideTimer) {
                clearTimeout(hideTimer)
                hideTimer = undefined
            }
        }

        const reconcile = () => {
            clearHideTimer()
            if (overSelf) {
                entityRenderable.onMouseOver(true)
                this.setHighlightedEntity(entity.entityId)
            } else {
                hideTimer = setTimeout(() => {
                    hideTimer = undefined
                    entityRenderable.onMouseOver(false)
                    this.clearHighlightIf(entity.entityId)
                }, INTERACTION_HOVER_GRACE_MS)
            }
        }

        const pointAt = () => {
            overSelf = true
            this.logic.requestEntityInteractions(entity.entityId)
            this.logic.playerController.setHoveredEntity(entity.entityId)
            reconcile()
        }

        renderable.on(isMobile() ? "tap" : "mouseover", () => {
            pointAt()

            if (isMobile()) {
                // Touch has no mouseout; release the entity after a delay instead.
                clearHideTimer()
                hideTimer = setTimeout(() => {
                    hideTimer = undefined
                    overSelf = false
                    this.logic.playerController.unHoveredEntity(entity.entityId)
                    reconcile()
                }, 1500)
            }
        })

        renderable.on("mouseout", () => {
            overSelf = false
            this.logic.playerController.unHoveredEntity(entity.entityId)
            reconcile()
        })

        // Secondary mouse clicks (including trackpad two-finger taps) open menus.
        // Touch waits for a long press; desktop primary presses belong to world attack.
        let touchOrigin: { x: number; y: number } | undefined
        renderable.on("pointerdown", (event: any) => {
            touchOrigin = event.data?.pointerType === "touch"
                ? { x: event.data.global.x, y: event.data.global.y } : undefined
            this.logic.playerController.setHoveredEntity(entity.entityId)
            this.logic.playerController.beginEntityPress(
                entity.entityId, event.data?.pointerType || "mouse", event.data?.button || 0,
            )
        })
        renderable.on("rightclick", (event: any) => {
            event.data?.originalEvent?.preventDefault?.()
        })
        renderable.on("pointermove", (event: any) => {
            if (touchOrigin && Math.hypot(event.data.global.x - touchOrigin.x, event.data.global.y - touchOrigin.y) > 12) {
                touchOrigin = undefined
                this.logic.playerController.cancelEntityPress(entity.entityId)
            }
        })
        renderable.on("pointerup", () => {
            touchOrigin = undefined
            this.logic.playerController.endEntityPress(entity.entityId)
        })
        renderable.on("pointercancel", () => {
            touchOrigin = undefined
            this.logic.playerController.cancelEntityPress(entity.entityId)
        })
        renderable.on("pointerupoutside", () => {
            touchOrigin = undefined
            this.logic.playerController.cancelEntityPress(entity.entityId)
        })

        // Entities the server has its own plans for on a plain click (collectables it picks up, the
        // outpost avatars) keep working, but only when they offer no interactions of their own --
        // otherwise a single tap would both activate an interaction and fire ENTITY_CLICKED, and the
        // two handlers would race. An entity with interactions reaches the server through
        // ACTIVATE_ENTITY_INTERACTION instead.
        const callback = () => {
            if (this.getEntityInteractions(entity.entityId).length > 0) {
                return
            }
            this.logic.entityClicked(entity)
        }
        renderable.on("click", callback)
        renderable.on("tap", callback)
    }

    // The interactions this viewer has been offered for an entity, most-primary first. Empty for an
    // entity that offers none, which is what makes a tap on it fall through to the world action.
    //
    // You are never a target of your own interactions: hovering yourself must not light you up, and a
    // press on yourself belongs to the world action (attack/build), not to an interaction. Filtered
    // here rather than upstream because the server really does offer the player entity interactions
    // of its own -- atoms/inventory.ts puts "inventory" on it -- and this is the single funnel the
    // cursor, the outline and the press arbitration all read.
    getEntityInteractions = (entityId: EntityId): InteractionDescriptor[] => {
        if (entityId === this.logic.playerEntity?.entityId) {
            return []
        }
        return this.entityIdToInteractions[entityId] || []
    }

    updateEntityInteractions = (entityId: EntityId, interactions: (InteractionDescriptor | InteractionType)[], colliding?: boolean) => {
        // Cached even when the renderable isn't ready yet, so attachClickHandler can re-apply it on
        // (re)creation -- see entityIdToInteractions. Normalized on the way in so a bare
        // InteractionType[] (older payload shape, or a test fixture) still works.
        this.entityIdToInteractions[entityId] = normalizeInteractions(interactions)
        this.applyInteractionAffordance(entityId)
        if (colliding === true) this.collidingInteractionEntities.add(entityId)
        if (colliding === false) this.collidingInteractionEntities.delete(entityId)
        this.updateDefaultInteractionWheel(entityId)
    }

    private updateDefaultInteractionWheel = (entityId: EntityId) => {
        const interaction = this.collidingInteractionEntities.has(entityId)
            ? rankInteractions(this.getEntityInteractions(entityId)).find(next => next.isDefault && !interactionIsHeld(next))
            : undefined
        const overlay = this.worldOverlaySurface.getEntityRenderable(entityId)
        let wheel = this.defaultInteractionWheels.get(entityId)
        if (!interaction || !overlay) {
            if (wheel) {
                wheel.emit("pointerout")
                wheel.destroy({ children: true })
                this.defaultInteractionWheels.delete(entityId)
                this.defaultInteractionLabels.delete(entityId)
            }
            return
        }
        if (!wheel) {
            wheel = new CommandWheel({
                onHoverChange: () => this.logic.audio.uiHover(),
                onCommandSelect: command => {
                    const current = this.getEntityInteractions(entityId)
                        .find(next => next.type === command && next.isDefault && !interactionIsHeld(next))
                    if (this.collidingInteractionEntities.has(entityId) && current) {
                        this.logic.audio.uiSelect()
                        this.logic.interactionCallback(entityId, current.type)
                    }
                },
            })
            let holdingMouse = false
            wheel.on("pointerover", () => {
                holdingMouse = true
                this.logic.playerController.enabledMouseAction(false)
            })
            wheel.on("pointerout", () => {
                if (holdingMouse) this.logic.playerController.enabledMouseAction(true)
                holdingMouse = false
            })
            this.defaultInteractionWheels.set(entityId, wheel)
        }
        if (wheel.parent !== overlay) overlay.addChild(wheel)
        const label = `${interaction.type}:${interactionLabel(interaction)}`
        if (this.defaultInteractionLabels.get(entityId) !== label) {
            wheel.bindToSingleInteraction(entityId, { name: interaction.type, displayName: interactionLabel(interaction) })
            wheel.fadeIn()
            this.defaultInteractionLabels.set(entityId, label)
        }
        wheel.position.set(TileSize / 4, -wheel.getExtent().bottom - 12)
    }

    // A pointer cursor is the whole desktop "you can do something here" cue now that the icons are
    // gone; the outline is the other half and comes on hover. Both are driven from the cache, so an
    // entity that gains or loses an interaction mid-session picks it up immediately -- including
    // losing the outline it is currently wearing.
    private applyInteractionAffordance = (entityId: EntityId) => {
        const renderable = this.entityIdToRenderable[entityId]
        if (!renderable) {
            return
        }
        const hasInteractions = this.getEntityInteractions(entityId).length > 0
        const clickSurface = renderable.getClickSurface()
        if (clickSurface) {
            clickSurface.cursor = hasInteractions ? "pointer" : null
            const entity = this.logic.getEntity(entityId)
            if (entity) {
                clickSurface.interactive = this.shouldTakePointer(entity)
            }
        }
        if (!hasInteractions && this.highlightedEntityId === entityId) {
            this.clearHighlightIf(entityId)
        }
    }

    // Whether this entity should take pointer events at all.
    //
    // Pixi hands a pointer to the TOPMOST interactive object, so an entity with nothing to offer
    // doesn't merely waste a hit test -- it HIDES whatever is under it. The ember campfire sat under
    // two such things at once: its flame is a separate decorative marker drawn over the logs, and an
    // invisible ember manager entity (the wave/spawner/tutorial controllers are all `blank-32x32.png`
    // markers parked on the camp) sits on the same tile. Between them they swallowed every hover
    // aimed at the fire, leaving only the sliver of the logs' hit box poking out past them -- which
    // is why the campfire read as having a hover target a few pixels tall.
    //
    // So a MARKER has to earn the pointer: by offering an interaction, or by having visible art that
    // a click means something on. Everything else keeps it unconditionally -- players and NPCs show a
    // name plate and HP bar on hover even when they offer nothing to activate.
    private shouldTakePointer = (entity: Entity): boolean => {
        if (this.getEntityInteractions(entity.entityId).length > 0) {
            return true
        }
        if (entity.npcType !== "marker") {
            return true
        }
        // A blank stand-in sprite is either tile-drawn art (a chest -- which has interactions, so it
        // never reaches here) or an invisible controller. Neither is something to point at.
        const isBlank = !entity.spriteId || entity.spriteId.startsWith("blank")
        return !!entity.clickable && !isBlank
    }

    // Only the pointed-at (or, on touch, focused) entity is outlined, and only when it has something
    // to activate -- an outline on an entity a tap would just swing at would be a lie.
    private setHighlightedEntity = (entityId: EntityId) => {
        if (this.getEntityInteractions(entityId).length === 0) {
            this.clearHighlightIf(this.highlightedEntityId)
            return
        }
        if (this.highlightedEntityId === entityId) {
            return
        }
        this.clearHighlightIf(this.highlightedEntityId)
        this.highlightedEntityId = entityId

        // A renderable that reports false has no art of its own to trace -- it is an invisible marker
        // standing on tile-drawn art (the V2 chest). Outline those map tiles instead, so a chest gets
        // the same glow as a person rather than a box drawn round its footprint.
        if (this.applyHighlight(entityId) === false) {
            this.tileHighlight().set(true)
        }
    }

    private clearHighlightIf = (entityId?: EntityId) => {
        if (!entityId || this.highlightedEntityId !== entityId) {
            return
        }
        this.highlightedEntityId = undefined
        // Not simply "off": the roster may still want this one outlined.
        this.applyHighlight(entityId)
        this.entityTileHighlight?.set(false)
    }

    // The ember roster's selected member wears the same outline as a pointed-at entity, so the card
    // and the character on screen are visibly the same person. Undefined takes it off. The glow only
    // shows while the member is actually drawn: culling strips it (see hideRenderable) and coming
    // back into view puts it back (see renderEntity), which is what makes it viewport-bound.
    setRosterHighlight = (entityId?: EntityId) => {
        if (this.rosterHighlightId === entityId) {
            return
        }
        const previous = this.rosterHighlightId
        this.rosterHighlightId = entityId
        this.applyHighlight(previous)
        this.applyHighlight(entityId)
        // The deselected member may be dropping back to a red problem disc rather than to nothing,
        // which is why this recomputes the tier for both rather than just clearing the old one.
        this.applyStatusHalo(previous)
        this.applyStatusHalo(entityId)
    }

    // Does this entity have ANY reason left to be outlined? Two channels feed the one glow, and the
    // renderable only holds a single on/off, so the union has to be worked out here.
    private highlightWanted = (entityId: EntityId): boolean =>
        this.highlightedEntityId === entityId || this.rosterHighlightId === entityId

    // Push that union onto one entity's renderable. Returns what the renderable reported (false =
    // no art of its own to trace), or undefined when there is nothing to push it onto. Cheap to call
    // repeatedly: EntityHighlight.set is a no-op when nothing has changed.
    private applyHighlight = (entityId?: EntityId): boolean | undefined => {
        if (!entityId) {
            return undefined
        }
        return this.entityIdToRenderable[entityId]?.setHighlighted(this.highlightWanted(entityId))
    }

    // WHICH GROUND DISC THIS BODY WEARS, if any. Two channels feed the one disc and the renderable
    // holds a single tier, so -- exactly like `highlightWanted` above -- the union is worked out
    // here rather than on the sprite.
    //
    // SELECTION WINS over a problem. The red disc is ambient: it has been there a while and will
    // still be there in a minute. The green one answers a question the player asked half a second
    // ago by tapping a card ("which of these people is that?"), and if a hungry member's card were
    // the one tapped, leaving them red would withhold the one answer they are waiting for. The card
    // they are looking at still carries the status pill saying what is wrong, so nothing is lost by
    // the red disc standing down while they look.
    private statusHaloTierFor = (entityId: EntityId): StatusHaloTier | undefined =>
        this.rosterHighlightId === entityId
            ? "selected"
            : statusHaloTier(this.logic.getEmberStatus(entityId))

    // Push that union onto one entity's disc, and remember what was pushed so the per-frame entity
    // pass does not have to ask again. Safe to call for an entity with no renderable yet: the cache
    // is keyed independently, so the tier is simply recomputed when one turns up.
    private applyStatusHalo = (entityId?: EntityId) => {
        if (!entityId) {
            return
        }
        const cached = this.localEntityMetaCache[entityId]
        const tier = this.statusHaloTierFor(entityId)
        if (cached && cached.statusHaloTier === tier) {
            return
        }
        if (cached) {
            cached.statusHaloTier = tier
        }
        this.entityIdToRenderable[entityId]?.updateStatusHalo(tier)
    }

    // The outline used for entities drawn as map tiles. One instance, reused: it reads the highlighted
    // entity's tiles each frame, so switching from one chest to another needs no teardown. It is
    // parented into the map's own tile surface, which is why the tile sprites' coordinates can be
    // copied straight across with no conversion.
    private tileHighlight = (): EntityHighlight => {
        if (!this.entityTileHighlight) {
            this.entityTileHighlight = new EntityHighlight({
                artContainer: this.logic.clientRenderer.map().getTileSurface(),
                frame: this.highlightedEntityTileFrames,
                // Under its own tiles in the z-sorted tile surface.
                zIndex: () => this.highlightedEntityTileZIndex,
            })
        }
        return this.entityTileHighlight
    }

    private highlightedEntityTileZIndex: number = 0

    // The map tiles standing in for the highlighted entity, as outline frames. Looked up per frame
    // rather than cached: a map rerender destroys and rebuilds these sprites, and a stale reference
    // would be a destroyed texture.
    private highlightedEntityTileFrames = (): HighlightFrame[] => {
        const entityId = this.highlightedEntityId
        if (!entityId) {
            return []
        }
        const entity = this.logic.getEntity(entityId)
        if (!entity) {
            return []
        }
        // Floor, not round: this is "which cell contains the entity". NB the fineToRoughCoordinates
        // exported further down this very file ROUNDS, which lands a marker sitting at its tile's
        // centre (a chest is at x*32 + 16) on the NEXT cell over -- where there are no tiles to trace.
        const tiles = this.logic.clientRenderer
            .map()
            .getSpritedTilesAt(Math.floor(entity.location.x / TileSize), Math.floor(entity.location.y / TileSize))
        if (tiles.length === 0) {
            return []
        }
        this.highlightedEntityTileZIndex = Math.min(...tiles.map(tile => tile.zIndex)) - 1
        return tiles.map(tile => ({
            texture: tile.texture,
            x: tile.x,
            y: tile.y,
            scaleX: tile.scale.x,
            scaleY: tile.scale.y,
        }))
    }

    // Touch substitute for hover: outline the interactable nearest the player, so a tap always has a
    // visible target. The mobile scan is throttled; collision wheels update on every platform.
    updateInteractionFocus = (deltaMs = 0) => {
        this.collidingInteractionEntities.forEach(this.updateDefaultInteractionWheel)
        this.defaultInteractionWheels.forEach(wheel => wheel.animate(deltaMs))
        if (!isMobile()) {
            return
        }
        const now = Date.now()
        if (now - this.lastFocusScanTs < FOCUS_SCAN_INTERVAL_MS) {
            return
        }
        this.lastFocusScanTs = now

        const player = this.logic.playerEntity
        if (!player) {
            return
        }

        let nearestId: EntityId | undefined
        let nearestDistance = FOCUS_RANGE
        Object.keys(this.entityIdToInteractions).forEach(entityId => {
            if (this.entityIdToInteractions[entityId].length === 0) {
                return
            }
            const entity = this.logic.getEntity(entityId)
            if (!entity || entity.entityId === player.entityId) {
                return
            }
            const dx = entity.location.x - player.location.x
            const dy = entity.location.y - player.location.y
            const distance = Math.sqrt(dx * dx + dy * dy)
            if (distance <= nearestDistance) {
                nearestDistance = distance
                nearestId = entity.entityId
            }
        })

        if (!nearestId) {
            this.clearHighlightIf(this.highlightedEntityId)
            return
        }
        this.setHighlightedEntity(nearestId)
    }

    // Newly created renderables start visible, so a creation that lands outside the culled set
    // has to be hidden here -- and one that lands inside has to join the shown set, or the
    // end-of-frame sweep would not know to hide it later.
    private trackCreated = (
        entity: Entity,
        renderable: ClientRenderable,
        worldOverlayItem?: { visible: boolean },
    ) => {
        if (entity.interestState !== "active" || this.logic.isTooDistantFromPlayer(entity)) {
            renderable.visible = false
            if (worldOverlayItem) {
                worldOverlayItem.visible = false
            }
            return
        }
        this.shownEntityIds.add(entity.entityId)
    }

    create = (entity: Entity): ClientRenderable => {
        this.cacheEntity(entity)
        const isClientPlayer = this.logic.playerEntity.entityId === entity.entityId

        if (entity.areaProjectile) {
            const renderable = new AreaProjectile(entity)
            this.entityIdToRenderable[entity.entityId] = renderable
            this.surface.addChild(renderable)
            this.trackCreated(entity, renderable)
            return renderable
        }

        if (entity.entityType === "player") {
            // Only this branch sees real player bodies (NPCs build their LpcComposite in
            // renderEntity), so it is where the "a person is driving this" ground ring is decided:
            // every player except the one at this keyboard.
            const renderable: ClientRenderable = this.takeLevelEntity(entity) ?? new LpcComposite(
                entity.entityId,
                undefined,
                isClientPlayer,
                !isClientPlayer,
            )

            this.entityIdToRenderable[entity.entityId] = renderable
            this.surface.addChild(renderable)
            if (entity.isPlayer()) {
                this.minimapSurface.offer(entity.entityId, isClientPlayer)
            }
            this.attachClickHandler(entity, renderable)
            const worldOverlayItem = this.worldOverlaySurface.offer(entity)
            renderable.setWorldOverlayItem(worldOverlayItem)

            if (!this.reusedLevelArt.delete(renderable) && !entity.floorFall?.fromAbove && !entity.stairTravel) renderable.fadeIn()

            this.trackCreated(entity, renderable, worldOverlayItem)

            return renderable
        }

        if (entity.entityType === "bullet") {
            if (entity.spriteId !== "none") {
                const renderable: ClientRenderable = this.createBulletArtwork()
                // const renderable = new BasicSprite({
                //     name: entity.spriteId,
                //     scale: 1.0,
                //     color: 0xFF0000,
                // })
                renderable.updateRenderedLocationFrom(entity)
                renderable.rotation = entity.movement.angle
                this.entityIdToRenderable[entity.entityId] = renderable

                this.surface.addChild(renderable)
                this.attachClickHandler(entity, renderable)
                return renderable
            }
        }

        if (entity.npcType === "collectable") {
            const renderable = this.createCollectableArtwork(entity)
            this.entityIdToRenderable[entity.entityId] = renderable

            this.surface.addChild(renderable)
            this.attachClickHandler(entity, renderable)
            // The discovery glint draws in the overlay, above the world, so loot under a canopy or
            // behind a wall still shows its lit shape. It traces THIS renderable's live transform
            // and texture, which is the only thing that stays right while a collectable spins.
            this.worldOverlaySurface.offerDroppedItemGlint(entity.entityId, renderable)

            this.trackCreated(entity, renderable)

            return renderable
        }

        if (entity.npcType === "marker") {
            if (entity.spriteId) {
                // Interactable markers need a hit surface sized to their artwork.
                const renderable = this.createMarkerArtwork(entity, true)
                this.entityIdToRenderable[entity.entityId] = renderable
                this.surface.addChild(renderable)
                this.attachClickHandler(entity, renderable)
                const worldOverlayItem = this.worldOverlaySurface.offer(entity)
                renderable.setWorldOverlayItem(worldOverlayItem)

                if (entity.spriteScale !== undefined) {
                    // A flame draws its burn-down as a lower fire, not a scaled one.
                    if (renderable instanceof FireSprite) renderable.updateScale(entity.spriteScale)
                    else renderable.scale.set(entity.spriteScale)
                }

                const tileMeta = this.logic.tilemapMetaProvider.resolveTileMeta(entity.spriteId)
                if (tileMeta) {
                    if (tileMeta.renderZIdxOffset) {
                        // renderable.defaultZIndex = tileMeta.renderZIdxOffset
                        renderable.defaultZIndex = tileMeta.renderZIdxOffset
                        renderable.zIndex = renderable.defaultZIndex
                    }
                }

                this.trackCreated(entity, renderable, worldOverlayItem)
                return renderable
            } else {
                const renderable = new Blank()
                this.entityIdToRenderable[entity.entityId] = renderable
                this.surface.addChild(renderable)
                this.attachClickHandler(entity, renderable)
                if (entity.spriteScale !== undefined) {
                    renderable.scale.set(entity.spriteScale)
                }
                return renderable
            }
        }

        if (entity.entityType === "bomb") {
            const renderable: ClientRenderable = new Bomb(entity)
            renderable.x = entity.location.x
            renderable.y = entity.location.y
            this.entityIdToRenderable[entity.entityId] = renderable

            this.surface.addChild(renderable)
            this.attachClickHandler(entity, renderable)
            return renderable
        }

        let renderable: ClientRenderable = this.renderEntity(entity)
        if (!renderable) {
            return
        }
        if (!this.reusedLevelArt.delete(renderable)) renderable.fadeIn()
        this.attachClickHandler(entity, renderable)

        let minimappable = false

        switch (entity.npcType) {
            case "flag":
            case "goal": {
                minimappable = true
                break
            }
            case "door": {
                minimappable = true
                break
            }
            default: {
                if (entity.isPlayer()) {
                    // if (entity.npcType === "zombie") {
                    minimappable = true
                } else {
                    minimappable = false
                }
            }
        }

        if (minimappable) {
            this.minimapSurface.offer(entity.entityId)
        }

        this.entityIdToRenderable[entity.entityId] = renderable
        this.surface.addChild(renderable)
        const worldOverlayItem = this.worldOverlaySurface.offer(entity)
        renderable.setWorldOverlayItem(worldOverlayItem)

        this.trackCreated(entity, renderable, worldOverlayItem)
        return renderable
    }

    private get = (entity: Entity): ClientRenderable | undefined => {
        return this.clientRenderer.map?.().getSceneRenderable?.(entity.entityId) ??
            this.entityIdToRenderable[entity.entityId]
    }

    private createBulletArtwork = () => new Bullet(0xffffff, 1, entity => {
        const owner = entity.ownerEntityId
        if (!owner) return undefined
        const source = this.clientRenderer.map?.().getSceneRenderable?.(owner) ?? this.entityIdToRenderable[owner]
        return source instanceof LpcComposite ? source.arrowLaunchOffset(entity.movement.angle) : undefined
    })

    private isClientPlayer = (entity: Entity) => entity?.entityId === this.clientRenderer.logic().playerEntity.entityId

    /**
     * The player's own body dissolving, and coming back.
     *
     * Kept HERE rather than on the renderable, because the point of it is to survive a room change:
     * a descent fades the body out in the camp and back in underground, and the renderable it fades
     * out is destroyed with the camp. The manager outlives both, so the fade is re-applied every
     * frame to whatever renderable the player currently has (see render()).
     *
     * `until` rather than a tween object for the same reason -- there is no frame loop running while
     * a room loads, so anything that advanced per-frame would arrive on the far side mid-dissolve.
     */
    private playerFade?: { from: number; to: number; startTs: number; ms: number }

    fadePlayer = (direction: "in" | "out", ms: number) => {
        const from = this.playerFadeAlpha() ?? 1
        this.playerFade = { from, to: direction === "in" ? 1 : 0, startTs: Date.now(), ms: Math.max(1, ms) }
    }

    /** Where the fade has got to, or undefined when there is none and the body is simply drawn. */
    private playerFadeAlpha = (): number | undefined => {
        const fade = this.playerFade
        if (!fade) {
            return undefined
        }
        const t = Math.min(1, (Date.now() - fade.startTs) / fade.ms)
        const alpha = fade.from + (fade.to - fade.from) * t
        if (t >= 1 && fade.to === 1) {
            // Done, and done at full opacity: drop the override so `entity.visible` owns the body
            // again. A finished fade-OUT is kept, because holding the body at 0 is its whole job.
            this.playerFade = undefined
        }
        return alpha
    }

    updateEntityId = (originalEntityId: EntityId, updatedEntityId: EntityId) => {
        this.entityIdToRenderable[updatedEntityId] = this.entityIdToRenderable[originalEntityId]
        delete this.entityIdToRenderable[originalEntityId]

        this.localEntityMetaCache[updatedEntityId] = this.localEntityMetaCache[originalEntityId]
        delete this.localEntityMetaCache[originalEntityId]

        this.entityIdToInteractions[updatedEntityId] = this.entityIdToInteractions[originalEntityId]
        delete this.entityIdToInteractions[originalEntityId]

        const colliding = this.collidingInteractionEntities.delete(originalEntityId)
        this.updateDefaultInteractionWheel(originalEntityId)
        if (colliding) this.collidingInteractionEntities.add(updatedEntityId)
        this.worldOverlaySurface.updateEntityId(originalEntityId, updatedEntityId)
    }

    isSubmerged = (entity: Entity) => {
        return this.logic.tilemapMetaProvider?.isDeepAt(
            Math.floor(entity.location.x / TileSize),
            Math.floor(entity.location.y / TileSize),
        )
    }

    update = (entity: Entity) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }
        const minimapRenderable = this.minimapSurface.get(entity.entityId)

        let cachedEntity: EntityMeta = this.localEntityMetaCache[entity.entityId]
        const wasCached = !!cachedEntity
        if (!cachedEntity) {
            this.cacheEntity(entity)
        }

        if (cachedEntity?.name !== entity.name) {
            renderable.updateName(entity.name || "")
            cachedEntity.name = entity.name

            if (this.isClientPlayer(entity)) {
                this.clientRenderer.gui().update(entity)
            }
        }

        if (cachedEntity?.color !== entity.color) {
            renderable.recolor(tintOf(entity.color))
            cachedEntity.color = entity.color
        }

        if (cachedEntity?.spriteScale !== entity.spriteScale) {
            renderable.updateScale(entity.spriteScale)
            cachedEntity.spriteScale = entity.spriteScale
        }

        Object.assign(cachedEntity, updateEntityArtwork(renderable, entity, cachedEntity))

        if (cachedEntity?.clickable !== entity.clickable) {
            this.attachClickHandler(entity, renderable)
            cachedEntity.clickable = entity.clickable
        }

        if (cachedEntity.submerged !== entity.submerged) {
            renderable.updateSubmerged(entity.submerged)
            cachedEntity.submerged = entity.submerged
        }

        // A player who starts or stops wearing one of a camp's attackers. Diffed like `submerged`
        // above rather than set every frame, and it has to be here at all because possession happens
        // to a sprite that already exists: the ring is built at construction from `remotePlayer`, and
        // a possessor is still a player entity, so without this the defender gets a player ring under
        // one of the sixteen things attacking their camp. See Entity.invading.
        if (cachedEntity.invading !== entity.invading) {
            renderable.updatePlayerRing(entity)
            cachedEntity.invading = entity.invading
        }

        if (this.isPaperdollDifferent(cachedEntity, entity)) {
            cachedEntity.apperanceKey = this.computeAppearanceKey(entity)
            renderable.updatePaperdoll(entity)
        }

        if (cachedEntity?.hp !== entity.hp) {
            renderable.update(entity.hp, entity.maxHp)
            if (this.isClientPlayer(entity)) {
                this.clientRenderer.gui().update(entity)
            }
            cachedEntity.hp = entity.hp
        }

        if (cachedEntity?.hunger !== entity.hunger) {
            renderable.update(entity.hp, entity.maxHunger)
            if (this.isClientPlayer(entity)) {
                this.clientRenderer.gui().update(entity)
            }
            cachedEntity.hunger = entity.hunger
        }

        if (cachedEntity?.maxHp !== entity.maxHp) {
            renderable.update(entity.hp, entity.maxHp)
            if (this.isClientPlayer(entity)) {
                this.clientRenderer.gui().update(entity)
            }
            cachedEntity.maxHp = entity.maxHp
        }

        if (cachedEntity?.mana !== entity.mana) {
            if (this.isClientPlayer(entity)) {
                this.clientRenderer.gui().update(entity)
            }
            cachedEntity.mana = entity.mana
        }

        // DOWNED has to be in this list, because the HUD draws a fallen player's health as empty
        // whatever their hp says (see GuiManager.update) -- and standing back up restores hp and
        // clears `downed` in two SEPARATE messages. On "Try again" the hp change landed first, so
        // the HUD was told "fallen, draw zero" with a full hp number, and when `downed` cleared a
        // moment later nothing in here was watching it: no further call, and the hearts stayed
        // empty for the rest of the run. Every value GuiManager.update reads must be able to
        // trigger it, not just the ones that happen to be numbers.
        if (cachedEntity?.downed !== entity.downed) {
            if (this.isClientPlayer(entity)) {
                this.clientRenderer.gui().update(entity)
            }
            cachedEntity.downed = entity.downed
        }

        if (cachedEntity?.upgradePoints !== entity.upgradePoints) {
            if (this.isClientPlayer(entity)) {
                this.clientRenderer.gui().update(entity)
            }
            cachedEntity.upgradePoints = entity.upgradePoints
        }

        if (cachedEntity?.speed !== entity.speed) {
            const factor = entity.speed / entity.maxSpeed
            renderable.updateSpeed(factor)
            cachedEntity.speed = entity.speed
        }

        if (cachedEntity?.upgradePoints !== entity.upgradePoints) {
            if (this.isClientPlayer(entity)) {
                this.clientRenderer.gui().update(entity)
            }
            cachedEntity.upgradePoints = entity.upgradePoints
        }

        if (cachedEntity?.joinedTeam !== entity.joinedTeam) {
            cachedEntity.joinedTeam = entity.joinedTeam
            if (entity.joinedTeam) {
                renderable.addTag()
            } else {
                renderable.removeTag()
            }
        }

        if (cachedEntity?.sigil !== entity?.flags[SIGIL_FLAG]) {
            cachedEntity.sigil = entity?.flags[SIGIL_FLAG]
            renderable.updateSigil(cachedEntity.sigil)
        }

        if (cachedEntity.visible !== entity.visible) {
            if (!entity.visible) {
                renderable.recolor(0xffffff80)
                if (minimapRenderable) {
                    minimapRenderable.visible = false
                }
            } else {
                renderable.recolor(tintOf(entity.color))
                if (minimapRenderable) {
                    minimapRenderable.visible = true
                }
            }
            cachedEntity.visible = entity.visible
            if (this.isClientPlayer(entity)) {
                // renderable.updateBlinking(!entity.visible)
                if (entity.visible) {
                    renderable.alpha = 1.0
                } else {
                    renderable.alpha = 0.0
                }
            }
        }

        if (cachedEntity.afk !== entity.afk) {
            cachedEntity.afk = entity.afk
            renderable.updateAfk(cachedEntity.afk)
        }

        const labelLevel = this.clientRenderer.map().getVisibility()?.enableCommandWheel ? 0 : entity.level || 1
        if (cachedEntity.level !== labelLevel) {
            renderable.updateLevel(labelLevel)
            cachedEntity.level = labelLevel
        }
        // Ember-camp role glyph (lord/defender/forager). Role isn't on the synced entity, but the
        // client already keeps an entityId->role map from the roster broadcast (getEmberRole).
        const role = this.logic.getEmberRole(entity.entityId)
        if (cachedEntity.role !== role) {
            cachedEntity.role = role
            renderable.updateRole(role)
        }
        // And the in-world ground halo that marks a member the player has to do something about.
        // Diffed off the same roster mirror as the role above, and for the same reason: neither is a
        // field on the synced entity. See MemberStatusHalo for what it is and what it deliberately
        // is not.
        this.applyStatusHalo(entity.entityId)
        // Fallen bodies sit on the floor, below standing entities regardless of their feet's y.
        // Recompute each update so getting back up restores normal depth sorting immediately.
        if (Mechanics.entity.body.isFlat(entity) || entity.downed || entity.floorFall?.phase === "landed" || entity.rescuePose === "downed") {
            renderable.zIndex = 0
        } else {
            const zIndexBoost = Mechanics.entity.body.calculateZIndexBoost(entity)
            renderable.zIndex = zIndexBoost + renderable.defaultZIndex || 0
        }
        renderable.zIndex = floorFallZIndex(entity, renderable.zIndex)

        const locationChanged = !(cachedEntity.x === entity.location.x && cachedEntity.y === entity.location.y)
        const entityLight = this.lights.get(entity.entityId)
        const lightChanged =
            !this.lights.contains(entity.entityId) ||
            entityLight?.intensity !== entity.lightIntensity() ||
            entityLight?.radius !== entity.lightRadius()

        if (locationChanged) {
            entity.submerged = this.isSubmerged(entity)
        }

        if (
            !this.isClientPlayer(entity) && ((entity.lightMeta && (locationChanged || lightChanged)) ||
            (entity.npcType === "light" && lightChanged))
        ) {
            this.lights.set(entity.entityId, {
                entityId: entity.entityId,
                location: entity.location,
                intensity: entity.lightIntensity(),
                radius: entity.lightRadius(),
            })
            cachedEntity.x = entity.location.x
            cachedEntity.y = entity.location.y
        }
        if (this.isClientPlayer(entity)) {
            cachedEntity.x = entity.location.x
            cachedEntity.y = entity.location.y
        }
        if (!wasCached || cachedEntity?.leaderEntityId !== entity.leaderEntityId) {
            if (this.logic.playerEntity.entityId !== entity.leaderEntityId) {
                // renderable.updateBlinking(true)
                renderable.updateHalo(entity, false)
            } else {
                // renderable.updateBlinking(false)
                renderable.updateHalo(entity, true)
            }
            cachedEntity.leaderEntityId = entity.leaderEntityId
        }

        // The countdown dial over a held-interaction target (see HoldTimer). NOT diffed against the
        // cache like everything above it: the remaining time is a different number on every
        // message, which is the point -- it is what keeps the ring anchored to the server's hold.
        this.worldOverlaySurface.updateHoldTimer(entity)
        // ...and the prompt that precedes it on an injured rescue survivor (see RescueHint). Same
        // channel, same reason: the server decides when it is up, and it says so on every message.
        this.worldOverlaySurface.updateRescueHint(entity)
    }

    /** Recreate visible art at the next render boundary, preserving gameplay and interaction data.
     * Retired texture generations stay alive while transient effects can still reference them. */
    refreshContent = () => {
        const interactions = { ...this.entityIdToInteractions }
        const colliding = new Set(this.collidingInteractionEntities)
        const roster = this.rosterHighlightId
        const highlighted = this.highlightedEntityId
        for (const id of Object.keys(this.entityIdToRenderable)) {
            const entity = this.logic.getEntity(id)
            if (!entity) { this.destroy({ entityId: id } as Entity); continue }
            const arrivalStarted = this.preSpawnEffects.has(id) || this.completedPreSpawnEffects.has(id)
            this.destroy(entity)
            if (arrivalStarted) this.completedPreSpawnEffects.add(id)
            this.entityIdToInteractions[id] = interactions[id] || []
            if (colliding.has(id)) this.collidingInteractionEntities.add(id)
            this.create(entity)
            // create() caches the entity as it is NOW, so update() would see nothing to apply and
            // the fresh renderable would never be dressed, named or given its hp bar (a spawn gets
            // these because its appearance lands in a later message). An empty cache makes every
            // field new again.
            this.localEntityMetaCache[id] = {} as EntityMeta
            this.update(entity)
        }
        this.rosterHighlightId = roster
        this.highlightedEntityId = highlighted
        for (const id of Object.keys(this.entityIdToRenderable)) this.entityIdToRenderable[id].setHighlighted?.(this.highlightWanted(id))
    }

    destroy = (entity: Entity) => {
        if (entity.entityId === this.logic.playerEntity?.entityId) this.playerOcclusionOutline.hide()
        delete this.localEntityMetaCache[entity.entityId]
        delete this.entityIdToInteractions[entity.entityId]
        this.collidingInteractionEntities.delete(entity.entityId)
        this.updateDefaultInteractionWheel(entity.entityId)
        this.shownEntityIds.delete(entity.entityId)
        this.renderedThisFrame.delete(entity.entityId)
        this.minimapSurface.remove(entity.entityId)

        // Release hover state so a removed entity can't leave the hover reference (used for click
        // auto-travel and press arbitration) pointing at a stale id, or world mouse actions stuck
        // disabled because it vanished mid-press.
        this.logic.playerController.clearHoverForEntity(entity.entityId)
        this.clearHighlightIf(entity.entityId)
        // A member who is gone is nobody's selection any more; leaving the id here would put the
        // glow on whatever entity is recycled into it.
        if (this.rosterHighlightId === entity.entityId) {
            this.rosterHighlightId = undefined
        }

        const renderable = this.entityIdToRenderable[entity.entityId]
        if (renderable) {
            const retained = this.retainLevelEntity(entity, renderable)
            // Clean up callbacks before destroying
            if (!retained && renderable.cleanup) {
                renderable.cleanup()
            }

            if (!retained) renderable.remove()
            delete this.entityIdToRenderable[entity.entityId]
            if (renderable.sideCar) {
                renderable.sideCar.remove()
            }
        }
        this.worldOverlaySurface.remove(entity)
        this.lights.remove(entity.entityId)
        this.preSpawnEffects.get(entity.entityId)?.destroy()
        this.preSpawnEffects.delete(entity.entityId)
        this.completedPreSpawnEffects.delete(entity.entityId)
        this.footDust.remove(entity.entityId)
        this.grassFeet.remove(entity.entityId)
    }

    /**
     * How many of the bodies on screen cannot draw themselves yet -- an LPC composite whose sheets
     * are still being fetched or baked is on the stage with nothing in it (see
     * LpcComposite.isReadyToShow).
     *
     * This is what a freshly loaded room is held black for: the count is read every frame while the
     * curtain is down and the screen opens when it reaches zero (see RoomReveal). "On screen" is the
     * shown set the culling pass maintains, so a camp is never held shut waiting for wildlife at the
     * other end of the map -- plus this client's own body, which render() deliberately leaves out of
     * that set and which is the one sprite the player is certain to be looking at.
     */
    isPlayerReadyToShow = (): boolean => {
        const player = this.entityIdToRenderable[this.logic.playerEntity?.entityId]
        return !!player && (player.isReadyToShow?.() ?? true)
    }

    pendingSpriteCount = (): number => {
        let pending = 0
        this.shownEntityIds.forEach(entityId => {
            const renderable = this.entityIdToRenderable[entityId]
            if (renderable?.isReadyToShow && !renderable.isReadyToShow()) {
                pending++
            }
        })

        const playerEntityId = this.logic.playerEntity?.entityId
        const player = playerEntityId ? this.entityIdToRenderable[playerEntityId] : undefined
        if (player?.isReadyToShow && !player.isReadyToShow()) {
            pending++
        }

        return pending
    }

    // Called by the render loop around its entity pass; see shownEntityIds above.
    beginFrame = () => {
        this.renderedThisFrame.clear()
    }

    private updateRenderedPlayerLight(entity: Entity, sprite: ClientRenderable): void {
        if (entity.entityId !== this.logic.playerEntity?.entityId) return
        // A map packet can precede its player packet. Old stair state must never
        // move the light back to the departing floor's unanimated origin.
        if (this.logic.roomId && (entity.roomId !== this.logic.roomId ||
            (entity.stairTravel && entity.stairTravel.roomId !== this.logic.roomId))) return
        // Stair and fall playback move the body independently of its simulation
        // coordinates. Carry its light with that body, including the floor handoff.
        // Remove the normal artwork lift to preserve the light's usual ground anchor.
        const location = { x: sprite.x, y: sprite.y - (sprite.getYOffset?.() ?? 0) }
        const intensity = entity.lightIntensity()
        const radius = entity.lightRadius()
        const previous = this.lights.get(entity.entityId)
        const handoff = this.playerLightHandoff
        if (handoff && previous) {
            const now = Date.now()
            if (!handoff.offset) {
                handoff.offset = { x: previous.location.x - location.x, y: previous.location.y - location.y }
                handoff.startTs = now
            }
            const progress = Math.min(1, (now - handoff.startTs) / 200)
            const remaining = 1 - progress * progress * (3 - 2 * progress)
            location.x += handoff.offset.x * remaining
            location.y += handoff.offset.y * remaining
            if (progress === 1) this.playerLightHandoff = undefined
        }
        if (!previous || previous.location.x !== location.x || previous.location.y !== location.y ||
            previous.intensity !== intensity || previous.radius !== radius) {
            this.lights.set(entity.entityId, { entityId: entity.entityId, location, intensity, radius })
        }
    }

    // Budget deferral skips expensive animation work, but must still place the sprite at the
    // current interpolated position and restore visibility after re-entry. No server-only flags
    // are needed to distinguish static from moving NPCs.
    markDeferred = (entityId: EntityId) => {
        const entity = this.logic.getEntity(entityId)
        if (!entity || entity.interestState !== "active" || this.logic.isTooDistantFromPlayer(entity)) {
            this.hideRenderable(entityId)
            return
        }
        this.renderedThisFrame.add(entityId)
        const renderable = this.entityIdToRenderable[entityId]
        if (renderable) {
            renderable.updateRenderedLocationFrom(entity)
            renderable.visible = entity.visible
            if (entity.floorFall || entity.stairTravel) {
                const fallView = floorFallForRendering(entity, renderable,
                    (this.logic.canRenderFloorFallArrival?.() ?? true) && (renderable.isReadyToShow?.() ?? true), Date.now(), this.logic.roomId)
                const boost = Mechanics.entity.body.calculateZIndexBoost(entity)
                renderable.zIndex = fallView.floorFall?.phase === "landed" ? 0 :
                    floorFallZIndex(fallView, boost + renderable.defaultZIndex || 0)
                renderable.doMovement(stairTravelForRendering(fallView, renderable, Date.now(), this.logic.roomId))
                applyFloorFall(fallView, renderable)
                applyStairTravel(entity, renderable, Date.now(), this.logic.roomId)
            } else {
                applyStairTravel(entity, renderable, Date.now(), this.logic.roomId)
            }
            this.updateRenderedPlayerLight(entity, renderable)
            this.updatePreSpawnEffect(entity, renderable)
            if (entity.visible) {
                this.shownEntityIds.add(entityId)
            } else {
                this.shownEntityIds.delete(entityId)
            }
        }
        this.worldOverlaySurface.updateEntity(entity)
    }

    endFrame = () => {
        // Lay down any footfalls that have come due and settle the dust already on the ground. It
        // runs before the early return below: a frame that rendered no entities still has to
        // advance a trail somebody left behind.
        this.footDust.update()
        this.grassFeet.beginFrame(this.logic.tilemapMetaProvider?.roomId)
        if ((window as any).grassFeet !== false) {
            for (const id of this.renderedThisFrame) {
                const renderable = this.entityIdToRenderable[id]
                if (!renderable?.visible) continue
                const feet = renderable.grassContact?.()
                if (!feet) continue
                const entity = this.logic.getEntity(id)
                if (!entity || !entity.visible || entity.submerged ||
                    entity.downed || !!entity.floorFall || entity.rescuePose === "downed" || Mechanics.entity.body.isFlat(entity)) continue
                this.grassFeet.contact(id, {
                    ...feet, zIndex: renderable.zIndex, alpha: renderable.alpha,
                    moving: !!entity.movement?.moving, angle: feet.angle ?? entity.movement?.angle,
                    travelAngle: entity.movement?.angle,
                })
            }
        }
        this.grassFeet.endFrame()
        this.preSpawnEffects.forEach((effect, id) => {
            effect.art.visible = this.entityIdToRenderable[id]?.visible && this.renderedThisFrame.has(id)
            effect.debris.visible = effect.art.visible
            if (effect.isExpired()) {
                effect.destroy()
                this.preSpawnEffects.delete(id)
                this.completedPreSpawnEffects.add(id)
            }
        })

        if (this.shownEntityIds.size < 1) {
            return
        }
        // hideRenderable only ever deletes the entry being visited, which is safe to do while
        // iterating a Set.
        for (const entityId of this.shownEntityIds) {
            if (this.renderedThisFrame.has(entityId)) {
                continue
            }
            this.hideRenderable(entityId)
        }
    }

    updatePlayerOcclusionOutline = () => {
        const player = this.logic.playerEntity
        const renderable = player && this.get(player)
        if (!player?.visible || !renderable?.visible ||
            !this.clientRenderer.map().hasForegroundSceneFloor(renderable.zIndex)) {
            this.playerOcclusionOutline.hide()
            return
        }
        this.playerOcclusionOutline.update(player?.visible && renderable?.visible
            ? renderable.occlusionBody?.() : undefined, renderable?.zIndex ?? 0,
            (bounds, z) => this.clientRenderer.map().isPlayerBehindScene(bounds, z))
    }

    // Hide an entity's sprite and overlay, and release any hover it was holding. Culling to
    // invisible never fires a pointer-out, so a stranded hover (name plate, outline) has to be
    // dropped by hand or it reappears without a hover when the entity comes back.
    private hideRenderable = (entityId: EntityId) => {
        this.shownEntityIds.delete(entityId)
        const renderable = this.entityIdToRenderable[entityId]
        if (renderable) {
            if (renderable.visible) {
                renderable.onMouseOver?.(false)
                renderable.setHighlighted?.(false)
                this.clearHighlightIf(entityId)
                this.logic.playerController?.clearHoverForEntity(entityId)
            }
            renderable.visible = false
        }
        const worldOverlayItem = this.worldOverlaySurface.getEntityRenderable(entityId)
        if (worldOverlayItem) {
            worldOverlayItem.visible = false
        }
    }

    private updatePreSpawnEffect(entity: Entity, renderable: ClientRenderable) {
        let effect = this.preSpawnEffects.get(entity.entityId)
        if (!effect && !this.completedPreSpawnEffects.has(entity.entityId) && entity.preSpawnEffectView?.type === "ground-rupture") {
            effect = new GroundRupture(this.surface, renderable, entity)
            this.preSpawnEffects.set(entity.entityId, effect)
            if (entity.hp > 0 && !entity.isDead() && entity.preSpawnEffectView.remainingMs > 0) {
                this.logic.audio.groundRupture(entity.location)
            }
        }
        if (effect && !effect.update(entity)) {
            effect.destroy()
            this.preSpawnEffects.delete(entity.entityId)
            this.completedPreSpawnEffects.add(entity.entityId)
        }
    }

    render = (entity: Entity, playerEntityId: EntityId) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        this.renderedThisFrame.add(entity.entityId)

        if (!this.logic.clientRenderer.map().getVisibility()?.suppressMinimap) {
            this.minimapSurface.updateEntity(entity.entityId, entity.roomId, entity.location)
        }

        // Check if entity is too far from player
        const isTooFar = entity.interestState !== "active" || this.logic.isTooDistantFromPlayer(entity)

        if (!isTooFar) {
            // Entity is close enough - make it visible and update
            if (entity.entityId !== playerEntityId) {
                renderable.visible = entity.visible
                if (entity.visible) {
                    this.shownEntityIds.add(entity.entityId)
                    // Roster statuses arrive separately from entity updates. A recruit parked at
                    // the fire may receive no entity changes until it is fed, so read the latest
                    // status while drawing it too. The tier cache avoids redundant sprite writes.
                    this.applyStatusHalo(entity.entityId)
                    // Re-assert the selected member's outline every frame they are drawn. A hover
                    // glow re-arms itself on the next pointer move; a roster selection has nothing
                    // to re-arm it, and two things throw it away behind its back -- culling takes it
                    // off by hand (see hideRenderable), and a paperdoll rebuild replaces the sprite
                    // that owns it (see LpcComposite.setHighlighted). Costs a map lookup: the
                    // highlight itself ignores a set() that changes nothing.
                    if (entity.entityId === this.rosterHighlightId) {
                        this.applyHighlight(entity.entityId)
                    }
                } else {
                    this.shownEntityIds.delete(entity.entityId)
                }
            }

            // The dissolve, re-asserted every frame it is running: the alpha written when
            // `entity.visible` changes is a one-shot, and a fade spans a room load and two
            // renderables. Skipped entirely while the player is HIDDEN, which owns alpha 0 for its
            // own reasons (see the entity.visible branch in update()).
            if (entity.entityId === playerEntityId && entity.visible) {
                const faded = this.playerFadeAlpha()
                if (faded !== undefined) {
                    renderable.alpha = faded
                }
            }

            renderable.updateRenderedLocationFrom(entity)
            const fallView = floorFallForRendering(entity, renderable,
                (this.logic.canRenderFloorFallArrival?.() ?? true) && (renderable.isReadyToShow?.() ?? true), Date.now(), this.logic.roomId)
            if (fallView.floorFall) {
                const boost = Mechanics.entity.body.calculateZIndexBoost(entity)
                renderable.zIndex = fallView.floorFall.phase === "landed" ? 0 :
                    floorFallZIndex(fallView, boost + renderable.defaultZIndex || 0)
            }
            renderable.doMovement(stairTravelForRendering(fallView, renderable, Date.now(), this.logic.roomId))
            applyFloorFall(fallView, renderable)
            applyStairTravel(entity, renderable, Date.now(), this.logic.roomId)
            this.updateRenderedPlayerLight(entity, renderable)
            this.updatePreSpawnEffect(entity, renderable)
            this.worldOverlaySurface.updateEntity(entity)
        } else {
            // Entity is too far - hide it
            if (entity.entityId !== playerEntityId) {
                this.hideRenderable(entity.entityId)
            } else {
                // Also hide the world overlay
                const worldOverlayItem = this.worldOverlaySurface.getEntityRenderable(entity.entityId)
                if (worldOverlayItem) {
                    worldOverlayItem.visible = false
                }
            }
        }

        return true
    }

    detonate = (entity: Entity) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        renderable.detonate()
    }

    emp = (entity: Entity, range: number) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        const surface = renderable.parent ?? this.surface
        const halo = new Graphics()
        surface.addChild(halo)

        halo.beginFill(0xff000080, 0.25)
        halo.drawCircle(1, 5, 5)
        halo.endFill()
        halo.x = renderable.x
        halo.y = renderable.y

        let scale = 1.0
        let roomId = entity.roomId
        const expandInterval = setInterval(() => {
            if (halo.destroyed || this.get(entity) !== renderable) {
                clearInterval(expandInterval)
                halo.parent?.removeChild(halo)
                return
            }
            scale += 0.5
            halo.scale.set(scale)
            halo.x = renderable.x - halo.width * 0.1
            halo.y = renderable.y - halo.height / 2
            if (halo.height > range) {
                clearInterval(expandInterval)
                let alpha = 1.0

                let startTs = Date.now()
                const contractInterval = setInterval(() => {
                    const changedRooms = roomId !== entity.roomId || halo.destroyed || this.get(entity) !== renderable
                    if (Date.now() - startTs > 3000 || changedRooms) {
                        clearInterval(contractInterval)
                        if (changedRooms) {
                            halo.parent?.removeChild(halo)
                        } else {
                            const fadeInterval = setInterval(() => {
                                if (halo.destroyed) { clearInterval(fadeInterval); return }
                                alpha -= 0.01
                                halo.alpha = alpha
                                if (alpha <= 0.0) {
                                    clearInterval(fadeInterval)
                                    halo.parent?.removeChild(halo)
                                }
                            }, 5)
                        }
                    }
                }, 250)
            }
        }, 4)
    }

    // Green "sparkle halo": an expanding green glow ring plus a handful of twinkling star
    // sparkles ringing the entity, all fading over ~700ms. Server-triggered (see
    // Protocol.type_entitySparkle) as feedback when a campfire is refreshed with wood.
    sparkle = (entity: Entity) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        const surface = renderable.parent ?? this.surface
        const roomId = entity.roomId
        const color = 0x33ff66
        const cx = renderable.x
        // Nudge up toward the middle of the sprite.
        const cy = renderable.y - TileSize / 2

        // --- Expanding green glow ring ---
        const ring = new Graphics()
        surface.addChild(ring)
        ring.lineStyle(3, color, 0.9)
        ring.beginFill(color, 0.15)
        ring.drawCircle(0, 0, TileSize * 0.6)
        ring.endFill()
        ring.x = cx
        ring.y = cy

        const ringStart = Date.now()
        const ringDuration = 700
        const ringInterval = setInterval(() => {
            const t = (Date.now() - ringStart) / ringDuration
            if (t >= 1 || roomId !== entity.roomId || this.get(entity) !== renderable) {
                clearInterval(ringInterval)
                ring.parent?.removeChild(ring)
                return
            }
            ring.x = renderable.x
            ring.y = renderable.y - TileSize / 2
            ring.scale.set(0.6 + t * 1.6)
            ring.alpha = 1 - t
        }, 16)

        // --- Twinkling star sparkles ringing the entity ---
        const sparkleCount = 7
        for (let i = 0; i < sparkleCount; i++) {
            const angle = (Math.PI * 2 * i) / sparkleCount + Math.random() * 0.4
            const radius = TileSize * (0.5 + Math.random() * 0.35)
            const star = this.drawSparkleStar(color, 3 + Math.random() * 2)
            surface.addChild(star)
            let offsetX = Math.cos(angle) * radius
            let offsetY = Math.sin(angle) * radius
            star.x = cx + offsetX
            star.y = cy + offsetY

            const driftX = Math.cos(angle) * 0.3
            const driftY = Math.sin(angle) * 0.3 - 0.4 // slight upward bias
            const start = Date.now()
            const life = 500 + Math.random() * 300
            const twinklePhase = Math.random() * Math.PI * 2
            const interval = setInterval(() => {
                const elapsed = Date.now() - start
                if (elapsed > life || roomId !== entity.roomId || this.get(entity) !== renderable) {
                    clearInterval(interval)
                    star.parent?.removeChild(star)
                    return
                }
                offsetX += driftX
                offsetY += driftY
                star.x = renderable.x + offsetX
                star.y = renderable.y - TileSize / 2 + offsetY
                const fade = 1 - elapsed / life
                star.alpha = fade * (0.5 + 0.5 * Math.abs(Math.sin(elapsed / 60 + twinklePhase)))
                star.rotation += 0.08
            }, 16)
        }
    }

    // A small 4-point sparkle drawn from two overlapping diamonds (drawPolygon is core in
    // pixi v6; drawStar is not).
    private drawSparkleStar = (color: number, size: number): Graphics => {
        const g = new Graphics()
        g.beginFill(color, 1)
        // Vertical spike
        g.drawPolygon([0, -size * 2, size * 0.5, 0, 0, size * 2, -size * 0.5, 0])
        // Horizontal spike
        g.drawPolygon([-size * 2, 0, 0, size * 0.5, size * 2, 0, 0, -size * 0.5])
        g.endFill()
        return g
    }

    attack = (entity: Entity, callback?: Callback<void>) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        renderable.doAttack(entity, callback)
    }

    updateActivation = (entity: Entity, active: boolean) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        renderable.updateActivation(entity, active)
    }

    animateStrikeOn = (target: Entity) => {
        const struckEffect = new Other(0xff0000, 10)
        const renderable = this.get(target)
        if (!renderable) {
            return
        }
        // struckEffect.x = renderable.x
        // struckEffect.y = renderable.y
        // struckEffect.zIndex = renderable.zIndex + 1
        // this.surface.addChild(struckEffect)

        // struckEffect.alpha = 0.5

        // let startTS = Date.now()
        // let totalDuration = 150
        // let scale = 0
        // let incr = 0
        // const itr = setInterval(() => {
        //     const duration = Date.now() - startTS

        //     incr = duration > totalDuration / 2 ? -1 : 1
        //     scale += incr
        //     struckEffect.scale.set(scale, scale)
        //     if (duration > totalDuration) {
        //         clearInterval(itr)
        //         this.surface?.removeChild(struckEffect)
        //     }
        // }, 17)

        renderable.doDamaged(target)
    }

    spoke = (entity: Entity, message: string, options?: SpokenLineOptions) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        // Render instantly (no typewriter) when this entity is the follow-cam PiP focus — the
        // typewriter's progressive reveal flickers when rendered into the PiP texture. On the main
        // view (not the PiP focus) keep the typewriter.
        const instant = entity.entityId === this.logic.focusEntityId
        renderable.spoke(message, {
            instant,
            hold: options?.hold,
            autoAdvance: options?.autoAdvance,
            onDismissed: options?.onDismissed,
        })
    }

    // Called off a conversation the player has opted out of (the ember tutorial's Skip): every
    // held bubble on screen comes down, with whatever was queued behind it. Room-wide rather than
    // per-speaker, because what is being ended is the SCENE, not one entity's line.
    releaseHeldSpeech = () => {
        Object.values(this.entityIdToRenderable).forEach(renderable => renderable?.releaseHeldSpeech())
    }

    death = (entity: Entity) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        renderable.fragment(entity)
    }

    getLights = (): LightsContainer | undefined => {
        const companions = this.clientRenderer.map?.().getSceneLights?.() ?? []
        if (!companions.length && !this.sceneLights.size()) return this.lights
        const present = new Set<string>()
        const offer = (light: import("game-common/models").Light) => {
            present.add(light.entityId)
            const old = this.sceneLights.get(light.entityId)
            if (!old || old.location.x !== light.location.x || old.location.y !== light.location.y ||
                old.radius !== light.radius || old.intensity !== light.intensity) {
                this.sceneLights.set(light.entityId, { ...light, location: { ...light.location } })
            }
        }
        companions.forEach(offer)
        this.lights.iterate(offer)
        this.sceneLights.keys().forEach(id => { if (!present.has(id)) this.sceneLights.remove(id) })
        return this.sceneLights
    }

    clearBiomeColorMap = () => {
        this.minimapSurface.clearBiomeColorMap()
    }

    updateLocationIcons = (icon: LocationIcon) => {
        this.minimapSurface.updateLocationIcon(icon)
    }

    updatePlayerLocations = (playerLocations: Record<EntityId, Location>) => {
        const clientPlayerEntityId = this.clientRenderer.logic().playerEntity.entityId
        if (!clientPlayerEntityId) {
            return
        }

        if (!playerLocations) {
            return
        }

        this.minimapSurface.entities().forEach(entityId => {
            // remove any players not present in the latest
            // player location manifest sent from the server
            const entity = this.logic.entityManager.get(entityId)
            if (!playerLocations[entityId] || entity?.roomId !== this.clientRenderer.logic().playerEntity.roomId) {
                this.minimapSurface.remove(entityId)
            }
        })

        Object.keys(playerLocations).forEach((entityId: EntityId) => {
            const location = playerLocations[entityId]

            if (location?.quadrantId && location.quadrantId !== this.clientRenderer.logic().quadrantId) {
                return
            }

            this.minimapSurface.offer(entityId)

            this.minimapSurface.updateEntity(
                entityId,
                location.roomId,
                {
                    x: location.x,
                    y: location.y,
                },
                true,
            )
        })
    }

    pulseLocation = (locationId: string, expires?: number) => {
        this.minimapSurface.pulseLocation(locationId, expires)
    }

    updateEntityPointerIcon = (entity: Entity, show: boolean, angle?: number) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        renderable.updatePointerIcon(show, angle)
    }

    itemPickedUp = (entity: Entity, itemTypeMeta: ItemTypeMeta, quantity: number) => {
        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        renderable.itemPickedUp(entity, itemTypeMeta, quantity)
    }

    feedback = (entity: Entity, image: string, label: string, params: AnimatedFeedbackParams) => {
        // A running body's dust trail is not overlay feedback like a nameplate or a floating "+1":
        // it is art laid on the ground that has to sort against the bodies around it, so it is drawn
        // in the world surface this manager owns instead of inside the entity's renderable. See
        // FootDust for why the renderable cannot get the z-order right.
        if (params?.type === "foot") {
            if (params.action === "remove") {
                this.footDust.stop(entity.entityId)
            } else {
                this.footDust.start(entity, params, undefined, this.get(entity)?.parent ?? this.surface)
            }
            return
        }

        const renderable = this.get(entity)
        if (!renderable) {
            return
        }

        renderable.feedback(entity, image, label, params)
    }

    fragment = (spriteId: string, location: Coordinate) => {
        const renderable = new BasicSprite({
            name: spriteId,
            scale: 1.0,
            color: 0xffffff,
        })

        const fragmentContainer = new Graphics()
        fragmentContainer.x = location.x
        fragmentContainer.y = location.y - renderable.height * 0.25
        fragmentContainer.addChild(renderable)
        this.surface.addChild(fragmentContainer)

        renderable.fragment(null, () => {
            renderable.visible = false
        })

        setTimeout(() => {
            this.surface.removeChild(fragmentContainer)
        }, 1500)
    }
}

export const fineToRoughCoordinates = (fineCoordinate: Coordinate) => {
    return { x: Math.round(fineCoordinate.x / TileSize), y: Math.round(fineCoordinate.y / TileSize) }
}
